import { PageHero, Reveal, SectionHead, PlaceholderTag } from "../components/common";
import { VALUES, LEADERSHIP, ORG } from "../data/site";

function Block({ title, text, index, tag = true }) {
  return (
    <Reveal delay={index * 0.08}>
      <div className="border-t border-charcoal/15 pt-8">
        <span className="font-serif text-6xl md:text-7xl font-light text-charcoal/10">0{index + 1}</span>
        <h3 className="mt-4 font-serif text-2xl md:text-3xl font-medium tracking-tight">{title}</h3>
        <p className="mt-4 text-sm md:text-base leading-relaxed text-charcoal/70">{text}</p>
        {tag && <div className="mt-4"><PlaceholderTag text={typeof tag === "string" ? tag : undefined} /></div>}
      </div>
    </Reveal>
  );
}

export default function About() {
  return (
    <>
      <PageHero
        overline="About Us"
        title="A Trust Built on Responsibility"
        copy={`${ORG.name} is a registered charitable trust (Reg. No. ${ORG.registration}) based in ${ORG.location}, founded on ${ORG.founded}. ${ORG.objective}.`}
      />

      <section className="pb-24 md:pb-32">
        <div className="max-w-[90rem] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-3 gap-10">
          <Block index={0} title="Our Story" tag={false} text={`${ORG.name} was constituted by trust deed on 24 January 2017 by Mrs. Triza Jimmy Christie, and registered with the Charity Commissioner, Ahmedabad (Reg. No. ${ORG.registration}) on 18 March 2017. Founded on the conviction that no child's education should be cut short by poverty and no woman should face hardship alone, the Trust began its documented work in December 2017 with food kit and clothes distributions. Since then, its notebook drives, relief work and community support have reached students and families in more than sixty locations across Gujarat — work documented openly, year after year.`} />
          <Block index={1} title="Our Mission" tag={false} text={`${ORG.objective} — the Trust's stated objective since its earliest days.`} />
          <Block index={2} title="Our Vision" tag={false} text="Compassion in action: communities where no child's education is cut short by poverty, and no woman faces hardship alone." />
        </div>
      </section>

      <section className="py-24 md:py-32 bg-softgray">
        <div className="max-w-[90rem] mx-auto px-6 md:px-12">
          <SectionHead
            overline="What Guides Us"
            title="Our Values"
            copy="Drawn from the objectives written into the Trust's own deed of 24 January 2017 and reflected in nine years of documented work."
          />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v, i) => (
              <Reveal key={v.name} delay={i * 0.08} className="h-full">
                <div data-testid={`value-card-${i}`} className="bg-white border border-charcoal/5 rounded-2xl p-8 h-full flex flex-col gap-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                  <span className="font-serif text-5xl font-light text-terracotta/60">0{i + 1}</span>
                  <h4 className="font-serif text-xl font-medium text-charcoal">{v.name}</h4>
                  <p className="text-sm leading-relaxed text-charcoal/60">{v.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-[90rem] mx-auto px-6 md:px-12">
          <SectionHead
            overline="Leadership"
            title="Trustees & Team"
            copy="The Trust's serving trustees. Short biographies will be published once officially supplied."
          />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {LEADERSHIP.map((p, i) => (
              <Reveal key={i} delay={i * 0.08} className="h-full">
                <article data-testid={`leadership-card-${i}`} className="bg-white border border-charcoal/5 rounded-2xl overflow-hidden h-full shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                  <div className="aspect-[4/5] bg-softgray overflow-hidden">
                    {p.image ? (
                      <img
                        src={p.image}
                        alt={`${p.name}, ${p.role} of Susanna Charitable Trust`}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <PlaceholderTag text="Photograph to be provided" />
                      </div>
                    )}
                  </div>
                  <div className="p-7 space-y-3">
                    <h4 className="font-serif text-2xl font-medium tracking-tight">{p.name ?? "Name to be provided"}</h4>
                    <p className="text-xs font-mono uppercase tracking-widest text-charcoal/50">{p.role ?? "Position to be provided"}</p>
                    <p className="text-sm leading-relaxed text-charcoal/60">
                      {p.bio ?? "A short biography will be published here once supplied by the Trust."}
                    </p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
